import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Video, Download, Trash2, Search, X, ChevronDown, Loader2,
    AudioLines, Clock, Calendar, Play, ChevronRight, RefreshCw,
    Tag, ArrowLeft, Globe, Scissors, HardDriveDownload,
    Upload, Files, ChevronLeft, CheckCircle2, AlertCircle, Pencil, WifiOff
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/AuthService';
import VideoOverdub from '../components/VideoOverdub';
import VideoEditor from '../components/VideoEditor';
import AsinVideoBadge from '../components/AsinVideoBadge';

const API_BASE = 'https://server.multisellerpro.com/api';
const PAGE_SIZE = 18;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatTotalDuration = (seconds) => {
    if (!seconds) return '0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};

const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ─── Mobile-safe upload helpers ───────────────────────────────────────────────
// Phones freeze the tab when you switch apps / lock the screen. Failures that
// happen while hidden or offline are NOT counted; we wait and resume instead.

let uploadEpoch = 0;        // bumps every time the tab is hidden / goes offline
let interruptedAt = 0;
const inflightUploads = new Set();
const resumeWaiters = new Set();
let wakeLock = null;
let wakeWanted = false;

const tabActive = () => !document.hidden && navigator.onLine !== false;
const isMobileDevice = typeof navigator !== 'undefined' &&
    (navigator.userAgentData?.mobile ?? /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent));

const keepAwake = async (on) => {
    wakeWanted = on;
    try {
        if (on && !wakeLock && 'wakeLock' in navigator && !document.hidden) {
            wakeLock = await navigator.wakeLock.request('screen');
            wakeLock.addEventListener('release', () => { wakeLock = null; });
        } else if (!on && wakeLock) {
            await wakeLock.release();
            wakeLock = null;
        }
    } catch { /* not supported: fine */ }
};

const markInterrupted = () => { uploadEpoch++; interruptedAt = Date.now(); };
const markResumed = () => {
    if (!tabActive()) return;
    // Requests started before a long interruption were frozen by the OS: kill them so they retry now
    if (isMobileDevice && Date.now() - interruptedAt > 2000) {
        inflightUploads.forEach(r => { if (r.epoch !== uploadEpoch) r.controller.abort(); });
    }
    resumeWaiters.forEach(fn => fn());
    resumeWaiters.clear();
    if (wakeWanted) keepAwake(true); // browser drops the wake lock when hidden
};

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => (document.hidden ? markInterrupted() : markResumed()));
    window.addEventListener('offline', markInterrupted);
    window.addEventListener('online', markResumed);
}

const waitUntilActive = () =>
    tabActive() ? Promise.resolve() : new Promise(r => resumeWaiters.add(r));

// ─── Brave + human-readable upload errors ─────────────────────────────────────
const isBrave = typeof navigator !== 'undefined' && !!navigator.brave;

const uploadFail = (reason, extra = {}) => Object.assign(new Error(reason), { reason, ...extra });

const explainUploadError = (err) => {
    if (err?.reason) return err.reason;
    const msg = String(err?.message || err || '');
    if (err?.name === 'TypeError' || /failed to fetch|networkerror|load failed/i.test(msg)) {
        return isBrave
            ? 'Brave blocked the connection to our video storage. Click the lion icon 🦁 in the address bar and turn Shields OFF for this site (the page will reload), then upload this video again.'
            : 'The connection to our video storage was blocked or dropped. Check your internet, pause any ad-blocker or VPN for this site, then press "Retry failed".';
    }
    return `Unexpected error: ${msg || 'unknown'}. Press "Retry failed".`;
};

// Runs fn(signal) with a timeout. Retries real failures with backoff (1s,2s,4s,8s,16s);
// failures caused by hiding the tab / going offline are not counted.
const withRetry = async (fn, timeoutMs = 120000, maxFailures = 6) => {
    let failures = 0;
    let mustWait = false;
    for (; ;) {
        // Only pause when offline, or after a request died because the tab was hidden/frozen
        if (mustWait || navigator.onLine === false) await waitUntilActive();
        mustWait = false;
        const entry = { controller: new AbortController(), epoch: uploadEpoch, timedOut: false };
        const timer = setTimeout(() => { entry.timedOut = true; entry.controller.abort(); }, timeoutMs);
        inflightUploads.add(entry);
        try {
            return await fn(entry.controller.signal);
        } catch (err) {
            if (entry.epoch !== uploadEpoch || !tabActive()) { mustWait = true; continue; } // interrupted → not a real failure
            const realErr = entry.timedOut
                ? uploadFail(`The server didn't respond within ${Math.round(timeoutMs / 1000)}s. Your connection is probably too slow or unstable. Press "Retry failed" (finished parts are kept).`)
                : (err?.reason ? err : uploadFail(explainUploadError(err)));
            if (realErr.fatal || ++failures >= maxFailures) throw realErr;
            await new Promise(r => setTimeout(r, Math.min(16000, 1000 * 2 ** (failures - 1))));
        } finally {
            clearTimeout(timer);
            inflightUploads.delete(entry);
        }
    }
};

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, gradient }) => (
    <div className="bg-white rounded-xl shadow-md p-5 flex items-center gap-4 border border-gray-100 hover:shadow-lg transition-shadow">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${gradient}`}>
            <Icon className="text-white" size={22} />
        </div>
        <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
            {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        </div>
    </div>
);

// ─── Market Badge ─────────────────────────────────────────────────────────────

const MarketBadge = ({ market, domain, flag, exists, asin }) => (
    <a
        href={exists ? `https://www.amazon.${domain}/dp/${asin}` : undefined}
        target="_blank" rel="noopener noreferrer"
        onClick={e => { if (!exists) e.preventDefault(); }}
        style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '600',
            textDecoration: 'none',
            backgroundColor: exists ? '#dcfce7' : '#fee2e2',
            color: exists ? '#166534' : '#991b1b',
            border: `1px solid ${exists ? '#bbf7d0' : '#fecaca'}`,
            cursor: exists ? 'pointer' : 'default',
        }}
    >
        {flag} {market} {exists ? '✓' : '✗'}
    </a>
);

// ─── Video Card ───────────────────────────────────────────────────────────────

const VideoCard = ({ video, onDelete, onDownload, onOverdub, converting, onEdit, onEditTitle, onEditAsins, onStatusChange, user }) => {
    const [uploadedStatus, setUploadedStatus] = useState(video.status);
    const [openMenu, setOpenMenu] = useState(false);
    const [markets, setMarkets] = useState(null);
    const [checkingMarkets, setChecking] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    const isConverting = converting === video.id;

    const handleCheckMarkets = async () => {
        setChecking(true);
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/amazon/check-asin-markets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ asin: video.asin }),
            });
            const data = await res.json();
            setMarkets(data.markets);
        } catch (e) { console.error(e); }
        finally { setChecking(false); }
    };

    return (
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col">
            <div className="px-3 py-2.5 flex items-center gap-2.5 border-b border-gray-50">
                {video.product_image ? (
                    <a href={`https://www.amazon.com/dp/${video.asin}`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                        <img src={video.product_image} alt="" className="w-9 h-9 rounded-lg object-cover ring-1 ring-gray-200 hover:ring-blue-400 transition-all" />
                    </a>
                ) : (
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                        <Video size={15} className="text-blue-500" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <a href={`https://www.amazon.com/dp/${video.asin}`} target="_blank" rel="noopener noreferrer"
                        className="block text-xs font-semibold text-gray-800 truncate hover:text-blue-600 transition-colors leading-tight">
                        {video.product_title || 'Untitled Product'}
                    </a>
                    <div className="flex items-center gap-1.5 mt-0.5">
                        {video.review_title && <span className="text-[10px] text-gray-400 truncate max-w-[120px]">{video.review_title}</span>}
                        {video.product_price != null && <span className="text-[10px] font-bold text-emerald-600 flex-shrink-0">${video.product_price.toFixed(2)}</span>}
                    </div>
                </div>
                <div className="relative flex-shrink-0">
                    <button onClick={() => setOpenMenu(o => !o)}
                        className="flex items-center gap-1 px-2 py-1.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-[10px] font-semibold rounded-lg hover:opacity-90 transition shadow-sm">
                        Actions <ChevronDown size={10} />
                    </button>
                    {openMenu && (
                        <>
                            <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(false)} />
                            <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl border border-gray-100 shadow-2xl min-w-[140px] overflow-hidden py-1">
                                {[
                                    {
                                        label: isConverting ? 'Downloading…' : video.mp4_url ? 'Download MP4' : '⏳ Processing…',
                                        icon: isConverting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />,
                                        color: video.mp4_url ? '#2563eb' : '#9ca3af',
                                        disabled: isConverting || !video.mp4_url,
                                        action: () => { setOpenMenu(false); onDownload(video); },
                                    },
                                    { label: 'Re-record Audio', icon: <AudioLines size={12} />, color: '#7c3aed', action: () => { setOpenMenu(false); onOverdub(video); } },
                                    { label: 'Edit Video', icon: <Scissors size={12} />, color: '#0d9488', action: () => { setOpenMenu(false); onEdit(video); } },
                                    { label: 'Edit Title', icon: <Pencil size={12} />, color: '#8b5cf6', action: () => { setOpenMenu(false); onEditTitle(video); } },
                                    { label: 'Edit ASINs', icon: <Tag size={12} />, color: '#0891b2', action: () => { setOpenMenu(false); onEditAsins(video); } },
                                    {
                                        label: deleteConfirm ? 'Confirm Delete?' : 'Delete',
                                        icon: <Trash2 size={12} />, color: '#ef4444',
                                        action: () => { if (deleteConfirm) { setOpenMenu(false); onDelete(video.id); } else setDeleteConfirm(true); },
                                    },
                                ].map(item => (
                                    <button key={item.label} onClick={item.action} disabled={item.disabled}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-medium hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed text-left"
                                        style={{ color: item.disabled ? '#9ca3af' : item.color }}>
                                        {item.icon} {item.label}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="bg-black w-full">
                <video src={video.mp4_url || video.s3_url} controls className="w-full h-96 object-contain" preload="metadata" />
            </div>
            <div className="px-3 py-1.5 flex gap-1.5 bg-gray-50 border-t border-b border-gray-100">
                {[
                    { label: 'UPPER', value: `${video.upper_videos_per_fetch}=${parseFloat((video.upper_videos_per_fetch - video.upper_inf_per_fetch).toFixed(1))}+${video.upper_inf_per_fetch}` },
                    { label: 'BOTTOM', value: `${video.bottom_videos_per_fetch}=${parseFloat((video.bottom_videos_per_fetch - video.bottom_inf_per_fetch).toFixed(1))}+${video.bottom_inf_per_fetch}` },
                    { label: 'TOTAL INF.', value: video.total_inf_videos ?? '—' },
                    ...(video.duration_seconds != null ? [{ label: 'DUR', value: formatDuration(video.duration_seconds) }] : []),
                ].map(s => (
                    <div key={s.label} className="flex-1 text-center bg-white rounded-md py-1 px-1 border border-gray-100">
                        <div className="text-[8px] font-bold text-gray-400 tracking-wider">{s.label}</div>
                        <div className="text-[11px] font-bold text-gray-800 leading-tight">{s.value}</div>
                    </div>
                ))}
            </div>
            <div className="px-3 py-2 flex items-center gap-2 flex-wrap bg-white mt-auto">
                <button onClick={handleCheckMarkets} disabled={checkingMarkets}
                    className="flex items-center gap-1 px-2 py-0.5 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-300 text-white text-[9px] font-bold rounded transition">
                    {checkingMarkets ? <Loader2 size={9} className="animate-spin" /> : <Globe size={9} />}
                    {checkingMarkets ? 'Checking…' : '🌍 Markets'}
                </button>
                <AsinVideoBadge asin={video.asin} user={user} />
                {uploadedStatus === 'uploaded' ? (
                    <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-green-100 text-green-700 cursor-default">✓ Uploaded</span>
                ) : (
                    <button onClick={async () => {
                        try {
                            await authService.authenticatedFetch(`${API_BASE}/video/reviews/${video.id}`, {
                                method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ status: 'uploaded' }),
                            });
                            setUploadedStatus('uploaded');
                            onStatusChange?.(video.id, 'uploaded');
                        } catch (e) { console.error('Failed to mark as uploaded:', e); }
                    }} className="px-2 py-0.5 rounded text-[9px] font-bold bg-yellow-100 text-yellow-700 border border-yellow-300 hover:bg-yellow-100 hover:text-yellow-700 hover:border-green-300 transition cursor-pointer">
                        Mark as Uploaded
                    </button>
                )}
                {markets && (
                    <div className="flex gap-1">
                        <MarketBadge market="UK" domain="co.uk" flag="🇬🇧" exists={markets.UK} asin={video.asin} />
                        <MarketBadge market="CA" domain="ca" flag="🇨🇦" exists={markets.CA} asin={video.asin} />
                        <MarketBadge market="AU" domain="com.au" flag="🇦🇺" exists={markets.AU} asin={video.asin} />
                    </div>
                )}
                <div className="ml-auto flex items-center gap-2">
                    {video.asin && (
                        <a href={`https://www.amazon.com/dp/${video.asin}`} target="_blank" rel="noopener noreferrer"
                            className="text-[9px] font-mono text-gray-400 hover:text-blue-600 transition">{video.asin}</a>
                    )}
                    <span className="text-[9px] text-gray-400 flex-shrink-0">{formatDate(video.created_at)}</span>
                </div>
            </div>
        </div>
    );
};

// ─── Upload Meta Modal (title + ASINs) ───────────────────────────────────────

const UploadMetaModal = ({ file, onConfirm, onCancel, user }) => {
    const [title, setTitle] = useState(file?.name?.replace(/\.[^/.]+$/, '') || '');
    const [asinInput, setAsinInput] = useState('');
    const [asins, setAsins] = useState([]);
    const [showListPicker, setShowListPicker] = useState(false);
    const [linkedListItem, setLinkedListItem] = useState(null);

    const addAsins = () => {
        const newAsins = asinInput.split(',').map(a => a.trim().toUpperCase()).filter(a => a.length > 0);
        if (!newAsins.length) return;
        setAsins(prev => [...new Set([...prev, ...newAsins])]);
        setAsinInput('');
    };

    const removeAsin = (asin) => setAsins(prev => prev.filter(a => a !== asin));

    const handleListSelect = (items) => {
        const arr = Array.isArray(items) ? items : [items];
        setLinkedListItem(arr.length === 1 ? arr[0] : { isComparison: true, items: arr, asin: arr[0].asin, product_title: arr.map(i => i.product_title || i.asin).join(' vs '), product_image: arr[0].product_image });
        setShowListPicker(false);
        // Auto-fill title from first item
        if (arr[0].product_title && !title) setTitle(arr[0].product_title);
        // Add ALL selected ASINs — first = primary, rest = compare
        const newAsins = arr.map(i => i.asin).filter(Boolean).map(a => a.toUpperCase());
        if (newAsins.length) setAsins(prev => [...new Set([...prev, ...newAsins])]);
    };

    const handleConfirm = () => {
        onConfirm({
            title: title.trim() || file?.name?.replace(/\.[^/.]+$/, '') || 'Untitled',
            asin: asins[0] || null,
            compare_asins: asins.slice(1),
            linkedListItem: linkedListItem || null,
        });
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div>
                        <h2 className="text-base font-bold text-gray-800">Video Details</h2>
                        <p className="text-xs text-gray-400 truncate max-w-xs">{file?.name}</p>
                    </div>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>
                <div className="px-6 py-5 flex flex-col gap-4">
                    {/* Link to recording list */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            📋 Recording List <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        {linkedListItem ? (
                            <div className="flex items-center gap-2 p-2.5 bg-teal-50 border border-teal-200 rounded-xl">
                                {linkedListItem.product_image && (
                                    <img src={linkedListItem.product_image} alt="" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-teal-800 truncate">{linkedListItem.product_title || linkedListItem.asin}</p>
                                    <p className="text-[10px] font-mono text-teal-600">{linkedListItem.asin}</p>
                                </div>
                                <button onClick={() => setLinkedListItem(null)} className="text-teal-400 hover:text-red-400 flex-shrink-0"><X size={14} /></button>
                            </div>
                        ) : (
                            <button onClick={() => setShowListPicker(true)}
                                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-sm text-gray-400 hover:border-teal-400 hover:text-teal-600 transition font-medium">
                                📋 Pick from Recording List
                            </button>
                        )}
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            <Pencil size={11} className="inline mr-1" /> Video Title
                        </label>
                        <input
                            autoFocus
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleConfirm(); }}
                            placeholder="Enter a descriptive title…"
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                    </div>

                    {/* ASIN input */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            <Tag size={11} className="inline mr-1" /> ASINs <span className="text-gray-400 font-normal">(optional — comma separated)</span>
                        </label>
                        <div className="flex gap-2">
                            <input
                                value={asinInput}
                                onChange={e => setAsinInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAsins(); } }}
                                placeholder="e.g. B0C3TV6Q63, B0D444113X"
                                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono"
                            />
                            <button onClick={addAsins}
                                className="px-4 py-2 bg-purple-100 text-purple-700 text-sm font-semibold rounded-xl hover:bg-purple-200 transition">
                                Add
                            </button>
                        </div>
                        {asins.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {asins.map((asin, i) => (
                                    <span key={asin} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold border ${i === 0 ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                        {i === 0 && <span className="text-[9px] font-bold text-blue-500 mr-0.5">PRIMARY</span>}
                                        {asin}
                                        <button onClick={() => removeAsin(asin)} className="ml-0.5 hover:text-red-500"><X size={10} /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1">First ASIN = primary. Others = compare ASINs.</p>
                    </div>
                </div>
                <div className="flex gap-2 px-6 pb-5">
                    <button onClick={onCancel}
                        className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition font-medium">
                        Cancel
                    </button>
                    <button onClick={handleConfirm}
                        className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-violet-600 text-white text-sm font-semibold hover:opacity-90 transition shadow-sm">
                        Upload
                    </button>
                </div>
            </div>

            {/* Nested list picker */}
            {showListPicker && (
                <RecordingListPickerModal
                    user={user}
                    onClose={() => setShowListPicker(false)}
                    onMarkedRecorded={(items) => handleListSelect(items)}
                    selectOnly={true}
                />
            )}
        </div>
    );
};

// ─── Fail reason tooltip ──────────────────────────────────────────────────────
// position:fixed so the scrolling file list can't clip it; works on hover, focus and tap

const FailReason = ({ reason }) => {
    const [pos, setPos] = useState(null);
    const ref = useRef(null);
    const show = () => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const below = r.bottom + 200 < window.innerHeight;
        setPos({
            right: Math.max(8, window.innerWidth - r.right - 8),
            ...(below ? { top: r.bottom + 8 } : { bottom: window.innerHeight - r.top + 8 }),
        });
    };
    const hide = () => setPos(null);
    return (
        <>
            <button ref={ref} type="button" aria-label="Why did this fail?"
                onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} onClick={show}
                className="w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 shadow-sm cursor-help">
                ?
            </button>
            {pos && (
                <div style={{ position: 'fixed', zIndex: 100, ...pos }}
                    className="w-80 max-w-[90vw] bg-gray-900 text-white rounded-xl shadow-2xl p-4 pointer-events-none">
                    <div className="flex items-center gap-2 text-sm font-bold text-red-300 mb-1.5">
                        <AlertCircle size={15} /> Why this upload failed
                    </div>
                    <p className="text-[13px] leading-relaxed text-gray-100">{reason}</p>
                </div>
            )}
        </>
    );
};

// ─── Bulk Upload Modal ─────────────────────────────────────────────────────────

const BulkUploadModal = ({ onClose, onComplete, uploadSingleVideo, user }) => {
    const [step, setStep] = useState('pick'); // 'pick' | 'title' | 'upload'
    const [files, setFiles] = useState([]); // [{ file, title, status, progress }]
    const [titleIdx, setTitleIdx] = useState(0);
    const [titleInput, setTitleInput] = useState('');
    const [asinInput, setAsinInput] = useState('');
    const [linkedListItems, setLinkedListItems] = useState({});
    const [showListPicker, setShowListPicker] = useState(false);
    const fileInputRef = useRef();
    const jobsRef = useRef([]);           // remembers each file's S3 progress so Retry resumes
    const markedRef = useRef(new Set());  // recording-list items already marked
    const cancelledRef = useRef(false);
    const [running, setRunning] = useState(false);
    const [offline, setOffline] = useState(navigator.onLine === false);

    useEffect(() => {
        const on = () => setOffline(false);
        const off = () => setOffline(true);
        window.addEventListener('online', on);
        window.addEventListener('offline', off);
        return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
    }, []);

    // While uploading: keep screen awake + warn before leaving the page
    useEffect(() => {
        if (!running) return;
        const beforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', beforeUnload);
        keepAwake(true);
        return () => { window.removeEventListener('beforeunload', beforeUnload); keepAwake(false); };
    }, [running]);

    const handleFilePick = (e) => {
        const picked = Array.from(e.target.files || []);
        if (!picked.length) return;
        jobsRef.current = picked.map(() => ({}));
        markedRef.current = new Set();
        setFiles(picked.map(f => ({
            file: f,
            title: f.name.replace(/\.[^/.]+$/, ''),
            asins: [],
            status: 'pending',
            progress: 0,
        })));
        setTitleIdx(0);
        setStep('title');
    };

    // When entering title step for a new file, prefill
    useEffect(() => {
        if (step === 'title' && files[titleIdx]) {
            setTitleInput(files[titleIdx].title);
            setAsinInput('');
        }
    }, [titleIdx, step]);

    const saveTitle = () => {
        const trimmed = titleInput.trim() || files[titleIdx].file.name.replace(/\.[^/.]+$/, '');
        const updatedFiles = files.map((f, i) => i === titleIdx ? { ...f, title: trimmed } : f);
        setFiles(updatedFiles);
        if (titleIdx < files.length - 1) {
            setTitleIdx(i => i + 1);
        } else {
            setStep('upload');
            startUploads(updatedFiles);
        }
    };

    const skipTitle = () => {
        if (titleIdx < files.length - 1) {
            setTitleIdx(i => i + 1);
        } else {
            setStep('upload');
            startUploads(files);
        }
    };

    const goBack = () => {
        if (titleIdx > 0) setTitleIdx(i => i - 1);
    };

    const startUploads = async (filesToUpload, indices) => {
        const uploadList = filesToUpload || files;
        const order = indices || uploadList.map((_, i) => i);
        cancelledRef.current = false;
        setRunning(true);
        setStep('upload');
        for (const i of order) {
            if (cancelledRef.current) break;
            const item = uploadList[i];
            setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading' } : f));
            try {
                const reviewId = await uploadSingleVideo(item.file, item.title, (progress) => {
                    setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, progress } : f));
                }, { asin: item.asins?.[0] || null, compare_asins: item.asins?.slice(1) || [] }, jobsRef.current[i]);

                const linked = linkedListItems[i];
                if (linked && reviewId && !markedRef.current.has(i)) {
                    markedRef.current.add(i);
                    const itemsToMark = linked.isComparison && Array.isArray(linked.items) ? linked.items : [linked];
                    await Promise.all(itemsToMark.map(li =>
                        authService.authenticatedFetch(`${API_BASE}/video/recording-list/${li.id}/mark-recorded`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ review_id: reviewId }),
                        }).catch(() => { })
                    ));
                }

                setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'done', progress: 100 } : f));
            } catch (err) {
                setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', error: explainUploadError(err) } : f));
            }
        }
        setRunning(false);
        onComplete?.();
    };

    const retryFailed = () => {
        const failed = files.map((f, i) => (f.status === 'error' ? i : -1)).filter(i => i >= 0);
        if (failed.length) startUploads(files, failed);
    };

    // Clean up S3 parts of failed uploads we're giving up on (same as the old behavior)
    const abortFailedUploads = () => {
        jobsRef.current.forEach((job, i) => {
            if (files[i]?.status === 'error' && job?.upload_id && !job.completed) {
                authService.authenticatedFetch(`${API_BASE}/video/abort-upload`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ upload_id: job.upload_id, key: job.key }),
                }).catch(() => { });
            }
        });
    };

    const handleClose = () => {
        if (running && !window.confirm('Uploads are still in progress. The current video will finish, the rest will be cancelled. Close anyway?')) return;
        cancelledRef.current = true;
        abortFailedUploads();
        onClose();
    };

    const currentFile = files[titleIdx];
    const previewUrl = useMemo(
        () => (step === 'title' && currentFile ? URL.createObjectURL(currentFile.file) : null),
        [currentFile?.file, step]
    );
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
    const doneCount = files.filter(f => f.status === 'done').length;
    const errorCount = files.filter(f => f.status === 'error').length;
    const allDone = step === 'upload' && !running && files.length > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()} style={{ maxHeight: "85vh", overflowY: "auto" }}>

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                            <Files size={18} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-800">Bulk Upload</h2>
                            <p className="text-xs text-gray-400">
                                {step === 'pick' && 'Select multiple videos to upload'}
                                {step === 'title' && `Name video ${titleIdx + 1} of ${files.length}`}
                                {step === 'upload' && `Uploading ${files.length} video${files.length !== 1 ? 's' : ''}…`}
                            </p>
                        </div>
                    </div>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition">
                        <X size={20} />
                    </button>
                </div>

                {/* Step: Pick files */}
                {step === 'pick' && (
                    <div className="px-6 py-8 flex flex-col items-center gap-4">
                        {isBrave && (
                            <div className="w-full text-[12px] leading-snug text-orange-800 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2.5">
                                🦁 <b>Using Brave?</b> Before uploading, click the lion icon in the address bar and turn <b>Shields OFF</b> for this site. Otherwise some videos may fail.
                            </div>
                        )}
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full border-2 border-dashed border-purple-200 rounded-2xl p-10 flex flex-col items-center gap-3 cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-all group"
                        >
                            <div className="w-14 h-14 rounded-2xl bg-purple-100 group-hover:bg-purple-200 transition flex items-center justify-center">
                                <Upload size={24} className="text-purple-500" />
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-semibold text-gray-700">Click to select videos</p>
                                <p className="text-xs text-gray-400 mt-1">MP4, MOV, WebM, AVI — any number of files</p>
                            </div>
                        </div>
                        <input ref={fileInputRef} type="file" accept="video/*" multiple className="hidden" onChange={handleFilePick} />
                    </div>
                )}

                {/* Step: Title assignment */}
                {step === 'title' && currentFile && (
                    <div className="px-6 py-5 flex flex-col gap-4">
                        {/* Progress dots */}
                        <div className="flex gap-1.5 flex-wrap">
                            {files.map((f, i) => (
                                <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i < titleIdx ? 'bg-purple-400' : i === titleIdx ? 'bg-purple-600' : 'bg-gray-200'
                                    }`} style={{ minWidth: 12 }} />
                            ))}
                        </div>

                        {/* File preview info */}
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="w-full h-80 rounded-2xl overflow-hidden bg-black flex-shrink-0 border border-gray-200 shadow-sm">
                                <video
                                    src={previewUrl || undefined}
                                    className="w-full h-full object-contain"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload="auto"
                                />
                            </div>
                        </div>

                        {/* Title input */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                <Pencil size={11} className="inline mr-1" />
                                Video title
                            </label>
                            <input
                                autoFocus
                                value={titleInput}
                                onChange={e => setTitleInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') skipTitle(); }}
                                placeholder="Enter a descriptive title…"
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                            />
                            <p className="text-[10px] text-gray-400 mt-1">Press Enter to confirm, or skip to keep filename</p>

                            {/* Recording list picker */}
                            <div className="mt-3">
                                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                    📋 Recording List <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                {linkedListItems[titleIdx] ? (
                                    <div className="flex items-center gap-2 p-2 bg-teal-50 border border-teal-200 rounded-xl">
                                        {linkedListItems[titleIdx].product_image && (
                                            <img src={linkedListItems[titleIdx].product_image} alt="" className="w-7 h-7 rounded-lg object-contain flex-shrink-0" />
                                        )}
                                        <p className="flex-1 text-xs font-semibold text-teal-800 truncate">
                                            {linkedListItems[titleIdx].product_title || linkedListItems[titleIdx].asin}
                                        </p>
                                        <button onClick={() => setLinkedListItems(prev => { const n = { ...prev }; delete n[titleIdx]; return n; })}
                                            className="text-teal-400 hover:text-red-400"><X size={13} /></button>
                                    </div>
                                ) : (
                                    <button onClick={() => setShowListPicker(true)}
                                        className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border-2 border-dashed border-gray-200 text-xs text-gray-400 hover:border-teal-400 hover:text-teal-600 transition font-medium">
                                        📋 Pick from Recording List
                                    </button>
                                )}
                            </div>

                            {/* ASIN input */}
                            <div className="mt-3">
                                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                    <Tag size={11} className="inline mr-1" /> ASINs <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        value={asinInput}
                                        onChange={e => setAsinInput(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                const newAsins = asinInput.split(',').map(a => a.trim().toUpperCase()).filter(Boolean);
                                                if (!newAsins.length) return;
                                                setFiles(prev => prev.map((f, i) => i === titleIdx ? { ...f, asins: [...new Set([...(f.asins || []), ...newAsins])] } : f));
                                                setAsinInput('');
                                            }
                                        }}
                                        placeholder="e.g. B0C3TV6Q63"
                                        className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono"
                                    />
                                    <button onClick={() => {
                                        const newAsins = asinInput.split(',').map(a => a.trim().toUpperCase()).filter(Boolean);
                                        if (!newAsins.length) return;
                                        setFiles(prev => prev.map((f, i) => i === titleIdx ? { ...f, asins: [...new Set([...(f.asins || []), ...newAsins])] } : f));
                                        setAsinInput('');
                                    }} className="px-3 py-2 bg-purple-100 text-purple-700 text-xs font-semibold rounded-xl hover:bg-purple-200 transition">
                                        Add
                                    </button>
                                </div>
                                {(files[titleIdx]?.asins?.length > 0) && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                        {files[titleIdx].asins.map((asin, i) => (
                                            <span key={asin} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-mono font-semibold border ${i === 0 ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                                {i === 0 && <span className="text-[8px] text-blue-500">PRIMARY</span>}
                                                {asin}
                                                <button onClick={() => setFiles(prev => prev.map((f, fi) => fi === titleIdx ? { ...f, asins: f.asins.filter(a => a !== asin) } : f))}><X size={9} /></button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Navigation buttons */}
                        <div className="flex items-center gap-2">
                            {titleIdx > 0 && (
                                <button onClick={goBack}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition">
                                    <ChevronLeft size={13} /> Back
                                </button>
                            )}
                            <button onClick={skipTitle}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 transition ml-auto">
                                Skip
                            </button>
                            <button onClick={saveTitle}
                                className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-violet-600 text-white text-xs font-semibold hover:opacity-90 transition shadow-sm">
                                {titleIdx < files.length - 1 ? <>Next <ChevronRight size={13} /></> : <>Start Upload <Upload size={13} /></>}
                            </button>
                        </div>

                        {/* Remaining files list */}
                        {files.length > 1 && (
                            <div className="border-t border-gray-100 pt-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">All files</p>
                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {files.map((f, i) => (
                                        <div key={i} onClick={() => { if (i !== titleIdx) { setFiles(prev => prev.map((ff, fi) => fi === titleIdx ? { ...ff, title: titleInput.trim() || ff.file.name.replace(/\.[^/.]+$/, '') } : ff)); setTitleIdx(i); } }}
                                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition cursor-pointer ${i === titleIdx ? 'bg-purple-50 border border-purple-200' : 'hover:bg-gray-50'}`}>
                                            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${i < titleIdx ? 'bg-purple-100 text-purple-600' : i === titleIdx ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-400'
                                                }`}>{i + 1}</span>
                                            <span className="truncate flex-1 text-gray-600">{f.title || f.file.name.replace(/\.[^/.]+$/, '')}</span>
                                            {i < titleIdx && <CheckCircle2 size={11} className="text-purple-400 flex-shrink-0" />}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Step: Upload progress */}
                {step === 'upload' && (
                    <div className="px-6 py-5 flex flex-col gap-3">
                        {!allDone && (
                            <>
                                <div className="text-[11px] leading-snug text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                                    Keep this screen open until uploads finish. If you switch apps, uploads pause and continue when you come back.
                                </div>
                                {offline && (
                                    <div className="flex items-center gap-2 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                                        <WifiOff size={13} /> No connection — waiting to resume…
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                                    <Loader2 size={15} className="animate-spin text-purple-500" />
                                    Uploading {Math.max(1, files.findIndex(f => f.status === 'uploading') + 1)} of {files.length}…
                                </div>
                            </>
                        )}
                        {allDone && (
                            <div className="flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 rounded-xl px-4 py-2.5 mb-1">
                                <CheckCircle2 size={16} className="text-green-500" />
                                {doneCount} uploaded{errorCount > 0 ? `, ${errorCount} failed` : ' successfully'}
                            </div>
                        )}
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                            {files.map((f, i) => (
                                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border transition ${f.status === 'done' ? 'bg-green-50 border-green-200' :
                                    f.status === 'error' ? 'bg-red-50 border-red-200' :
                                        f.status === 'uploading' ? 'bg-purple-50 border-purple-200' :
                                            'bg-gray-50 border-gray-100'
                                    }`}>
                                    <div className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                                        {f.status === 'done' && <CheckCircle2 size={14} className="text-green-500" />}
                                        {f.status === 'error' && <AlertCircle size={14} className="text-red-500" />}
                                        {f.status === 'uploading' && <Loader2 size={14} className="animate-spin text-purple-500" />}
                                        {f.status === 'pending' && <Video size={14} className="text-gray-300" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-gray-700 truncate">{f.title}</p>
                                        <p className="text-[10px] text-gray-400 truncate">{f.file.name}</p>
                                        {f.status === 'uploading' && (
                                            <div className="mt-1.5 h-1 bg-purple-100 rounded-full overflow-hidden">
                                                <div className="h-full bg-gradient-to-r from-purple-500 to-violet-500 rounded-full transition-all duration-300"
                                                    style={{ width: `${f.progress}%` }} />
                                            </div>
                                        )}
                                    </div>
                                    {f.status === 'error' ? (
                                        <span className="flex items-center gap-1.5 flex-shrink-0">
                                            <span className="text-[10px] font-bold text-red-500">Failed</span>
                                            <FailReason reason={f.error || 'Unknown error. Press "Retry failed".'} />
                                        </span>
                                    ) : (
                                        <span className={`text-[10px] font-bold flex-shrink-0 ${f.status === 'done' ? 'text-green-600' :
                                            f.status === 'uploading' ? 'text-purple-600' : 'text-gray-300'}`}>
                                            {f.status === 'done' ? 'Done' : f.status === 'uploading' ? `${f.progress}%` : 'Waiting'}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                        {allDone && (
                            <div className="mt-2 flex gap-2">
                                {errorCount > 0 && (
                                    <button onClick={retryFailed}
                                        className="flex-1 py-2.5 rounded-xl border border-purple-300 text-purple-700 text-sm font-semibold hover:bg-purple-50 transition flex items-center justify-center gap-2">
                                        <RefreshCw size={14} /> Retry failed ({errorCount})
                                    </button>
                                )}
                                <button onClick={() => { abortFailedUploads(); onClose(); }}
                                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-violet-600 text-white text-sm font-semibold hover:opacity-90 transition shadow-sm">
                                    Done
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showListPicker && (
                <RecordingListPickerModal
                    user={user}
                    onClose={() => setShowListPicker(false)}
                    onMarkedRecorded={(items) => {
                        const arr = Array.isArray(items) ? items : [items];
                        const linked = arr.length === 1
                            ? arr[0]
                            : { isComparison: true, items: arr, asin: arr[0].asin, product_title: arr.map(i => i.product_title || i.asin).join(' vs '), product_image: arr[0].product_image };
                        setLinkedListItems(prev => ({ ...prev, [titleIdx]: linked }));
                        // Add all ASINs into that file's asins array
                        const newAsins = arr.map(i => i.asin).filter(Boolean).map(a => a.toUpperCase());
                        if (newAsins.length) {
                            setFiles(prev => prev.map((f, i) => i === titleIdx
                                ? { ...f, asins: [...new Set([...(f.asins || []), ...newAsins])] }
                                : f
                            ));
                        }
                        setShowListPicker(false);
                    }}
                    selectOnly={true}
                />
            )}
        </div>
    );
};

const RecordingListPickerModal = ({ user, onClose, onMarkedRecorded, selectOnly = false, standalone = false }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('list'); // 'list' | 'recorded' | 'all'
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    const [enlargedImage, setEnlargedImage] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        authService.authenticatedFetch(`${API_BASE}/video/recording-list`)
            .then(r => r.json())
            .then(async (data) => {
                const all = data.items || [];
                all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                setItems(all);
                setLoading(false);

                // Backfill missing title/image from Keepa, one at a time
                const missing = all.filter(i => i.asin && (!i.product_image || !i.product_title));
                for (const item of missing) {
                    try {
                        const r = await authService.authenticatedFetch(
                            `https://server.multisellerpro.com/api/ebay/keepa-product-data`,
                            {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ identifier: item.asin }),
                            }
                        );
                        if (!r.ok) continue;
                        const kData = await r.json();
                        if (kData.error) continue;
                        const updatedTitle = item.product_title || kData.title || item.asin;
                        const updatedImage = item.product_image || (kData.image_urls?.[0] ?? null);
                        setItems(prev => prev.map(i => i.id === item.id ? { ...i, product_title: updatedTitle, product_image: updatedImage } : i));
                        if (!item.product_title || !item.product_image) {
                            authService.authenticatedFetch(`${API_BASE}/video/recording-list/${item.id}/update-meta`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ product_title: updatedTitle, product_image: updatedImage }),
                            }).catch(() => { });
                        }
                    } catch { }
                }
            })
            .catch(() => setLoading(false));
    }, []);

    const toggle = (item) => {
        setSelected(prev => prev.some(s => s.id === item.id) ? prev.filter(s => s.id !== item.id) : [...prev, item]);
    };

    const handleRowClick = (item) => {
        if (standalone) {
            window.open(`https://www.amazon.com/dp/${item.asin}`, '_blank', 'noopener,noreferrer');
        } else {
            toggle(item);
        }
    };

    const resetItem = async (e, item) => {
        e.stopPropagation();
        try {
            await authService.authenticatedFetch(`${API_BASE}/video/recording-list/${item.id}/status`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 'pending' }),
            });
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'pending' } : i));
        } catch { }
    };

    const deleteItem = async (e, item) => {
        e.stopPropagation();
        try {
            await authService.authenticatedFetch(`${API_BASE}/video/recording-list/${item.id}`, { method: 'DELETE' });
            setItems(prev => prev.filter(i => i.id !== item.id));
            setSelected(prev => prev.filter(s => s.id !== item.id));
        } catch { }
    };

    const handleConfirm = async () => {
        if (!selected.length) return;
        if (selectOnly) {
            onMarkedRecorded?.(selected);
            return;
        }
        setSaving(true);
        await Promise.all(selected.map(item =>
            authService.authenticatedFetch(`${API_BASE}/video/recording-list/${item.id}/status`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 'recorded' }),
            }).catch(() => { })
        ));
        setSaving(false);
        onMarkedRecorded?.(selected);
        onClose();
    };

    const q = search.trim().toLowerCase();
    const filtered = items.filter(i => {
        const tabMatch = activeTab === 'all' ? true : activeTab === 'recorded' ? i.status === 'recorded' : i.status !== 'recorded';
        if (!tabMatch) return false;
        if (!q) return true;
        return (i.product_title || '').toLowerCase().includes(q) || (i.asin || '').toLowerCase().includes(q);
    });

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                    <div>
                        <h2 className="text-base font-bold text-gray-800">📋 My Video List</h2>
                        <p className="text-xs text-gray-400">
                            {standalone ? 'Everything in your recording list' : selectOnly ? 'Select the item this video belongs to' : "Select items you've already recorded manually"}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-100 flex-shrink-0">
                    {['list', 'recorded', 'all'].map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)}
                            className={`flex-1 py-2.5 text-xs font-bold capitalize transition border-b-2 ${activeTab === tab ? 'text-amber-600 border-amber-500' : 'text-gray-400 border-transparent hover:text-gray-600'}`}>
                            {tab === 'list' ? '📋 List' : tab === 'recorded' ? '🎬 Recorded' : '📚 All'}
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or ASIN…"
                            className="w-full pl-9 pr-4 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" />
                    </div>
                </div>

                {/* Lightbox */}
                {enlargedImage && (
                    <div onClick={() => setEnlargedImage(null)} className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center cursor-zoom-out">
                        <img src={enlargedImage} alt="" className="max-w-[85%] max-h-[85%] object-contain rounded-lg" />
                    </div>
                )}

                {/* Items */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                    {loading && (
                        <div className="text-center py-10 text-gray-400 text-sm flex items-center justify-center gap-2">
                            <Loader2 size={16} className="animate-spin" /> Loading…
                        </div>
                    )}
                    {!loading && filtered.length === 0 && (
                        <div className="text-center py-10 text-gray-400 text-sm">
                            {activeTab === 'recorded' ? 'No recorded videos yet.' : activeTab === 'all' ? 'Nothing here yet.' : 'Your list is empty.'}
                        </div>
                    )}
                    {filtered.map(item => {
                        const isChecked = selected.some(s => s.id === item.id);
                        const isSelectedStatus = item.status === 'selected';
                        return (
                            <div key={item.id} onClick={() => handleRowClick(item)}
                                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${isChecked ? 'bg-teal-50 border-teal-300' : isSelectedStatus ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100 hover:border-gray-300'}`}>
                                {!standalone && (
                                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition ${isChecked ? 'bg-teal-500 border-teal-500' : 'border-gray-300'}`}>
                                        {isChecked && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                                    </div>
                                )}
                                {item.product_image ? (
                                    <img src={item.product_image} alt=""
                                        onClick={e => { e.stopPropagation(); setEnlargedImage(item.product_image); }}
                                        className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-100 flex-shrink-0 cursor-zoom-in" />
                                ) : (
                                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-lg">📦</div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate">{item.product_title || item.asin}</p>
                                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                        <span className="text-[10px] font-mono text-gray-400">{item.asin}</span>
                                        <button onClick={e => {
                                            e.stopPropagation();
                                            navigator.clipboard.writeText(item.asin).catch(() => { });
                                            setCopiedId(item.id);
                                            setTimeout(() => setCopiedId(null), 1500);
                                        }} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${copiedId === item.id ? 'bg-teal-50 text-teal-600 border-teal-200' : 'bg-white text-gray-400 border-gray-200'}`}>
                                            {copiedId === item.id ? '✓' : 'Copy'}
                                        </button>
                                        {item.product_price && (
                                            <span className="text-[10px] font-mono text-emerald-600">${parseFloat(item.product_price).toFixed(2)}</span>
                                        )}
                                        {user && <AsinVideoBadge asin={item.asin} user={user} />}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                    {isSelectedStatus && (
                                        <>
                                            <span className="text-[9px] font-bold px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">IN PROGRESS</span>
                                            <button onClick={(e) => resetItem(e, item)}
                                                className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition">
                                                Reset
                                            </button>
                                        </>
                                    )}
                                    <button onClick={(e) => deleteItem(e, item)}
                                        className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-red-50 text-red-500 border border-red-100 hover:bg-red-100 transition">
                                        Remove
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                {!standalone && (
                    <div className="px-4 py-3 border-t border-gray-100 flex gap-2 flex-shrink-0">
                        <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition font-medium">
                            Cancel
                        </button>
                        <button onClick={handleConfirm} disabled={!selected.length || saving}
                            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-sm font-semibold hover:opacity-90 transition shadow-sm disabled:opacity-40 flex items-center justify-center gap-2">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                            {saving ? 'Saving…' : selectOnly ? `Select ${selected.length || ''}` : `Mark ${selected.length || ''} as Recorded`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const MyVideosDashboard = ({ user, compact = false, onViewAll }) => {
    const navigate = useNavigate();

    const [videos, setVideos] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');

    const [downloadingAll, setDownloadingAll] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [recordingListPickerOpen, setRecordingListPickerOpen] = useState(false);

    const [converting, setConverting] = useState(null);
    const [overdubReview, setOverdubReview] = useState(null);
    const [editReview, setEditReview] = useState(null);
    const [editTitleReview, setEditTitleReview] = useState(null);
    const [editTitleValue, setEditTitleValue] = useState('');
    const [editTitleSaving, setEditTitleSaving] = useState(false);
    const [editAsinsReview, setEditAsinsReview] = useState(null);
    const [editAsinsAll, setEditAsinsAll] = useState([]);
    const [editAsinsSelected, setEditAsinsSelected] = useState(new Set());
    const [editAsinsDetails, setEditAsinsDetails] = useState({});
    const [editAsinsCustomInput, setEditAsinsCustomInput] = useState('');
    const [editAsinsSaving, setEditAsinsSaving] = useState(false);
    const [editAsinsShowListPicker, setEditAsinsShowListPicker] = useState(false);
    const [recoveryOpen, setRecoveryOpen] = useState(false);
    const [recoveryFiles, setRecoveryFiles] = useState([]);
    const [recoveryPage, setRecoveryPage] = useState(1);
    const [recoveryLoading, setRecoveryLoading] = useState(false);
    const [recoveryDownloading, setRecoveryDownloading] = useState(null);
    const [recoveryPreview, setRecoveryPreview] = useState(null);
    const [recoveredFiles, setRecoveredFiles] = useState({});
    const [singleUploadFile, setSingleUploadFile] = useState(null);
    const recoveryPollRef = useRef(null);
    const mp4PollRef = useRef(null);

    // ── Fetch stats ──────────────────────────────────────────────────────────────

    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/video/stats`);
            const data = await res.json();
            setStats(data);
        } catch (e) { console.error(e); }
        finally { setStatsLoading(false); }
    }, []);

    // ── Fetch videos ─────────────────────────────────────────────────────────────

    const fetchVideos = useCallback(async (pg, q) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: pg, page_size: compact ? 6 : PAGE_SIZE });
            if (q) params.set('search', q);
            const res = await authService.authenticatedFetch(`${API_BASE}/video/reviews?${params}`);
            const data = await res.json();
            setVideos(prev => pg === 1 ? (data.reviews || []) : [...prev, ...(data.reviews || [])]);
            setHasMore(data.has_more);
        } catch (e) {
            console.error(e);
            if (pg === 1) setVideos([]);
        } finally { setLoading(false); }
    }, [compact]);

    useEffect(() => {
        fetchStats();
        fetchVideos(1, search);
    }, [search, fetchStats, fetchVideos]);

    // ── MP4 polling ───────────────────────────────────────────────────────────────

    useEffect(() => {
        const unconverted = videos.filter(v => !v.mp4_url);
        clearInterval(mp4PollRef.current);
        if (unconverted.length === 0) return;
        mp4PollRef.current = setInterval(async () => {
            try {
                const res = await authService.authenticatedFetch(`${API_BASE}/video/reviews?page=1&page_size=50`);
                const data = await res.json();
                setVideos(prev => prev.map(v => {
                    if (v.mp4_url) return v;
                    const updated = (data.reviews || []).find(r => r.id === v.id);
                    return updated?.mp4_url ? { ...v, mp4_url: updated.mp4_url } : v;
                }));
            } catch (e) { }
        }, 15000);
        return () => clearInterval(mp4PollRef.current);
    }, [videos]);

    // ── Client-side filter + sort ─────────────────────────────────────────────────

    const allCategories = useMemo(() =>
        [...new Set(videos.map(v => v.product_category).filter(Boolean))].sort(), [videos]);

    const displayVideos = useMemo(() => {
        let result = [...videos];
        if (categoryFilter !== 'all') result = result.filter(v => v.product_category === categoryFilter);
        if (sortBy === 'oldest') result.reverse();
        if (sortBy === 'longest') result.sort((a, b) => (b.duration_seconds || 0) - (a.duration_seconds || 0));
        if (sortBy === 'shortest') result.sort((a, b) => (a.duration_seconds || 0) - (b.duration_seconds || 0));
        return compact ? result.slice(0, 6) : result;
    }, [videos, categoryFilter, sortBy, compact]);

    // ── Single upload (reusable for bulk + individual) ────────────────────────────

    const abortMultipartUpload = async (upload_id, key) => {
        try {
            await authService.authenticatedFetch(`${API_BASE}/video/abort-upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ upload_id, key }),
            });
        } catch { /* best-effort cleanup, don't let this mask the real error */ }
    };

    const fetchAsinBasicInfo = async (asin) => {
        if (!asin) return null;
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/amazon/quick-batch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ asins: [asin] }),
            });
            const data = await res.json();
            return data.products?.[asin] || null;
        } catch {
            return null;
        }
    };

    const uploadSingleVideo = useCallback(async (file, reviewTitle, onProgress, meta = {}, job = null) => {
        const PART_SIZE = 8 * 1024 * 1024;
        const CONCURRENCY = isBrave ? 2 : 4;
        const PART_TIMEOUT_MS = isBrave ? 240000 : 120000;

        // `job` keeps progress between attempts (bulk Retry). Single upload has none → old behavior.
        const ownsJob = !job;
        const j = job || {};
        const totalParts = Math.max(1, Math.ceil(file.size / PART_SIZE));
        if (!j.etags) j.etags = new Array(totalParts).fill(null);
        if (j.saved) { onProgress?.(100); return j.reviewId; }

        if (!j.upload_id) {
            try {
                const data = await withRetry(async (signal) => {
                    const startRes = await authService.authenticatedFetch(`${API_BASE}/video/start-upload`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ content_type: file.type || 'video/mp4' }),
                        signal,
                    });
                    if (!startRes.ok) throw uploadFail(`server error ${startRes.status}${startRes.status === 401 ? ' (login expired, refresh the page and log in again)' : ''}`);
                    return startRes.json();
                });
                j.upload_id = data.upload_id;
                j.key = data.key;
            } catch (err) {
                throw uploadFail(`Couldn't start the upload: ${explainUploadError(err)}`);
            }
        }
        const { upload_id, key } = j;

        try {
            if (!j.completed) {
                let done = j.etags.filter(Boolean).length;
                onProgress?.(Math.round((done / totalParts) * 90));
                const queue = [];
                j.etags.forEach((e, i) => { if (!e) queue.push(i + 1); });
                let firstErr = null;

                const worker = async () => {
                    while (queue.length && !firstErr) {
                        const partNumber = queue.shift();
                        const chunk = file.slice((partNumber - 1) * PART_SIZE, partNumber * PART_SIZE);
                        try {
                            j.etags[partNumber - 1] = await withRetry(async (signal) => {
                                // fresh presigned URL every attempt
                                const presignRes = await authService.authenticatedFetch(`${API_BASE}/video/presign-part`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ upload_id, key, part_number: partNumber }),
                                    signal,
                                });
                                if (!presignRes.ok) throw uploadFail(`Our server couldn't prepare part ${partNumber} (error ${presignRes.status}). ${presignRes.status === 401 ? 'Your login expired. Refresh the page, log in, and upload again.' : 'Press "Retry failed" in a minute.'}`);
                                const { presigned_url } = await presignRes.json();
                                const s3r = await fetch(presigned_url, {
                                    method: 'PUT', body: chunk, headers: { 'Content-Type': '' }, signal,
                                    mode: 'cors', credentials: 'omit', cache: 'no-store',
                                });
                                if (!s3r.ok) throw uploadFail(`The storage server rejected part ${partNumber} (error ${s3r.status}). ${s3r.status === 403 ? 'The upload link expired. This usually means the device clock is wrong or the upload was paused too long.' : 'Temporary storage problem.'} Press "Retry failed".`);
                                const etag = s3r.headers.get('ETag');
                                if (!etag) throw uploadFail(
                                    isBrave
                                        ? 'Brave Shields hid the storage confirmation (ETag), so the video can\'t be assembled. Turn Shields OFF for this site (lion icon 🦁 in the address bar), then upload again.'
                                        : 'The storage server didn\'t return a confirmation (ETag). A browser extension may be blocking it. Disable it for this site and upload again.',
                                    { fatal: true }
                                );
                                return etag;
                            }, PART_TIMEOUT_MS);
                            done++;
                            onProgress?.(Math.round((done / totalParts) * 90));
                        } catch (err) {
                            firstErr = firstErr || err;
                        }
                    }
                };
                await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
                if (firstErr) throw firstErr;

                const parts = j.etags.map((ETag, i) => ({ PartNumber: i + 1, ETag }));
                await withRetry(async (signal) => {
                    const completeRes = await authService.authenticatedFetch(`${API_BASE}/video/complete-upload`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ upload_id, key, parts }),
                        signal,
                    });
                    if (!completeRes.ok) throw uploadFail(`All parts uploaded, but joining them failed (error ${completeRes.status}). Press "Retry failed". It won't re-send the video.`);
                    return completeRes.json();
                });
                j.completed = true;
            }

            const asinInfo = meta.asin ? await fetchAsinBasicInfo(meta.asin) : null;

            // maxFailures = 1: a real save error is not retried (avoids duplicate videos), same as before
            const saveData = await withRetry(async (signal) => {
                const saveRes = await authService.authenticatedFetch(`${API_BASE}/video/save-review`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        s3_key: key,
                        review_title: reviewTitle,
                        duration_seconds: null,
                        asin: meta.asin || null,
                        compare_asins: meta.compare_asins || [],
                        product_title: asinInfo?.title || null,
                        product_image: asinInfo?.image || null,
                        product_price: asinInfo?.price || null,
                        product_category: asinInfo?.category || null,
                    }),
                    signal,
                });
                if (!saveRes.ok) throw uploadFail(`Video uploaded, but saving it to your library failed (error ${saveRes.status}). Press "Retry failed". It won't re-upload the file.`);
                return saveRes.json();
            }, 120000, 1);

            j.saved = true;
            j.reviewId = saveData.id ?? saveData.review_id ?? null;
            onProgress?.(100);
            return j.reviewId;
        } catch (err) {
            // Single upload: clean up like before. Bulk: keep parts so "Retry failed" resumes.
            if (ownsJob && !j.completed) await abortMultipartUpload(upload_id, key);
            throw err;
        }
    }, []);

    // ── Handlers ──────────────────────────────────────────────────────────────────

    const handleSearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); };
    const clearSearch = () => { setSearchInput(''); setSearch(''); setPage(1); };
    const loadMore = () => { const next = page + 1; setPage(next); fetchVideos(next, search); };

    const handleDelete = async (id) => {
        try {
            await authService.authenticatedFetch(`${API_BASE}/video/reviews/${id}`, { method: 'DELETE' });
            setVideos(prev => prev.filter(v => v.id !== id));
            setStats(prev => prev ? { ...prev, total_videos: prev.total_videos - 1 } : prev);
        } catch (e) { console.error(e); }
    };

    const openEditTitle = (video) => {
        setEditTitleValue(video.review_title || '');
        setEditTitleReview(video);
    };

    const handleEditTitleSave = async () => {
        if (!editTitleReview) return;
        setEditTitleSaving(true);
        try {
            await authService.authenticatedFetch(`${API_BASE}/video/reviews/${editTitleReview.id}/title`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ review_title: editTitleValue.trim() }),
            });
            setVideos(prev => prev.map(v => v.id === editTitleReview.id ? { ...v, review_title: editTitleValue.trim() } : v));
            setEditTitleReview(null);
        } catch (e) {
            alert('Failed to save title. Please try again.');
        } finally {
            setEditTitleSaving(false);
        }
    };

    const openEditAsins = (video) => {
        const allAsins = [];
        if (video.asin) allAsins.push(video.asin);
        (video.compare_asins || []).forEach(a => { if (!allAsins.includes(a)) allAsins.push(a); });
        if (video.parent_asin && !allAsins.includes(video.parent_asin)) allAsins.push(video.parent_asin);
        (video.variation_asins || []).forEach(a => { if (!allAsins.includes(a)) allAsins.push(a); });

        const selected = new Set([video.asin, ...(video.compare_asins || [])].filter(Boolean));

        setEditAsinsReview(video);
        setEditAsinsAll(allAsins);
        setEditAsinsSelected(selected);
        setEditAsinsDetails({});
        setEditAsinsCustomInput('');

        (async () => {
            try {
                const response = await authService.authenticatedFetch(`${API_BASE}/amazon/quick-batch`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ asins: allAsins }),
                });
                const data = await response.json();
                setEditAsinsDetails(data.products || {});
            } catch (e) { console.error('Failed to fetch ASIN details:', e); }
        })();
    };

    const toggleEditAsin = (asin) => {
        setEditAsinsSelected(prev => {
            const next = new Set(prev);
            if (next.has(asin)) next.delete(asin);
            else if (next.size < 15) next.add(asin);
            return next;
        });
    };

    const handleAddCustomEditAsin = () => {
        const newAsins = editAsinsCustomInput.split(',').map(a => a.trim().toUpperCase()).filter(a => a.length > 0);
        if (newAsins.length === 0) return;
        setEditAsinsAll(prev => [...new Set([...prev, ...newAsins])]);
        setEditAsinsSelected(prev => new Set([...prev, ...newAsins].slice(0, 15)));
        setEditAsinsCustomInput('');
    };

    const handleEditAsinsSave = async () => {
        if (!editAsinsReview) return;
        setEditAsinsSaving(true);
        const selected = [...editAsinsSelected];
        const primaryAsin = selected[0] || null;
        const compareAsins = selected.slice(1);
        try {
            await authService.authenticatedFetch(`${API_BASE}/video/reviews/${editAsinsReview.id}/asins`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ asin: primaryAsin, compare_asins: compareAsins }),
            });
            setVideos(prev => prev.map(v => v.id === editAsinsReview.id ? { ...v, asin: primaryAsin, compare_asins: compareAsins } : v));
            setEditAsinsReview(null);
        } catch (e) {
            alert('Failed to save ASINs. Please try again.');
        } finally {
            setEditAsinsSaving(false);
        }
    };

    const handleDownload = async (video) => {
        if (!video.mp4_url) return;
        setConverting(video.id);
        try {
            const res = await fetch(video.mp4_url);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${video.review_title || video.product_title || 'video'}.mp4`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { console.error(e); }
        finally { setConverting(null); }
    };

    const handleDownloadAll = async () => {
        setDownloadingAll(true);
        try {
            let allVideos = [];
            let pg = 1;
            let more = true;
            while (more) {
                const params = new URLSearchParams({ page: pg, page_size: 50 });
                if (search) params.set('search', search);
                const res = await authService.authenticatedFetch(`${API_BASE}/video/reviews?${params}`);
                const data = await res.json();
                const batch = data.reviews || [];
                allVideos = [...allVideos, ...batch];
                more = data.has_more;
                pg++;
            }
            const downloadable = allVideos.filter(v => v.mp4_url);
            if (downloadable.length === 0) { alert('No MP4s ready to download yet.'); return; }
            for (const video of downloadable) {
                try {
                    const res = await fetch(video.mp4_url);
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${video.review_title || video.product_title || video.asin || 'video'}.mp4`;
                    a.click();
                    URL.revokeObjectURL(url);
                    await new Promise(r => setTimeout(r, 800));
                } catch (e) { console.error(e); }
            }
        } catch (e) { console.error(e); alert('Failed to fetch videos. Please try again.'); }
        finally { setDownloadingAll(false); }
    };

    const handleUploadVideo = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setSingleUploadFile(file);
    };

    const handleSingleUploadConfirm = async ({ title, asin, compare_asins, linkedListItem }) => {
        const file = singleUploadFile;
        setSingleUploadFile(null);
        setUploadingVideo(true);
        keepAwake(true);
        try {
            // uploadSingleVideo now returns the saved review id
            const reviewId = await uploadSingleVideo(file, title, undefined, { asin, compare_asins });

            // If user linked a recording list item, mark it as recorded
            if (linkedListItem && reviewId) {
                const itemsToMark = linkedListItem.isComparison && Array.isArray(linkedListItem.items)
                    ? linkedListItem.items
                    : [linkedListItem];
                await Promise.all(itemsToMark.map(item =>
                    authService.authenticatedFetch(`${API_BASE}/video/recording-list/${item.id}/mark-recorded`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ review_id: reviewId }),
                    }).catch(() => { })
                ));
            }

            fetchVideos(1, search);
            fetchStats();
        } catch (err) {
            console.error('Upload failed:', err);
            alert('Upload failed. Please try again.');
        } finally {
            keepAwake(false);
            setUploadingVideo(false);
        }
    };

    const handleOpenRecovery = async () => {
        setRecoveryOpen(true);
        setRecoveryLoading(true);
        try {
            const [s3Res, keysRes] = await Promise.all([
                authService.authenticatedFetch(`${API_BASE}/video/s3-webm-files`),
                authService.authenticatedFetch(`${API_BASE}/video/all-s3-keys`),
            ]);
            const s3Data = await s3Res.json();
            const keysData = await keysRes.json();
            const dbKeys = new Set(keysData.keys || []);
            const orphaned = (s3Data.files || []).filter(f => !dbKeys.has(f.s3_key));
            const sorted = orphaned.sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));
            setRecoveryFiles(sorted);
            setRecoveryPage(1);
        } catch (e) { console.error(e); }
        finally { setRecoveryLoading(false); }
    };

    const handleRecoveryRestore = async (file) => {
        setRecoveryDownloading(file.s3_key);
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/video/recover`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ s3_key: file.s3_key, s3_url: file.s3_url }),
            });
            const data = await res.json();
            if (res.ok && data.id) {
                setRecoveredFiles(prev => ({ ...prev, [file.s3_key]: { id: data.id, status: 'pending', mp4_url: null } }));
            }
        } catch (e) { console.error(e); }
        finally { setRecoveryDownloading(null); }
    };

    useEffect(() => {
        clearInterval(recoveryPollRef.current);
        const pending = Object.entries(recoveredFiles).filter(([, v]) => v.status !== 'done');
        if (pending.length === 0) return;
        recoveryPollRef.current = setInterval(async () => {
            try {
                const ids = pending.map(([, v]) => v.id);
                const res = await authService.authenticatedFetch(`${API_BASE}/video/convert-status?ids=${ids.join(',')}`);
                const data = await res.json();
                const records = data.records || [];
                setRecoveredFiles(prev => {
                    const next = { ...prev };
                    records.forEach(r => {
                        const key = Object.keys(next).find(k => next[k].id === r.id);
                        if (key) next[key] = { id: r.id, status: r.convert_status, mp4_url: r.mp4_url };
                    });
                    return next;
                });
            } catch (e) { console.error(e); }
        }, 10000);
        return () => clearInterval(recoveryPollRef.current);
    }, [recoveredFiles]);

    const handleRecoveryMp4Download = async (mp4_url, filename) => {
        try {
            const res = await fetch(mp4_url);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { console.error(e); }
    };

    const totalVideos = stats?.total_videos ?? videos.length;

    return (
        <div className={compact ? '' : 'min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4'}>
            <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        .video-card-enter { animation: fadeIn 0.25s ease-out; }
      `}</style>

            {!compact && (
                <div className="fixed inset-0 opacity-10 pointer-events-none overflow-hidden">
                    <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
                        style={{ background: 'linear-gradient(90deg,rgb(37,99,235),rgb(124,58,237))' }} />
                    <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full filter blur-3xl"
                        style={{ background: 'linear-gradient(90deg,rgb(37,99,235),rgb(124,58,237))' }} />
                </div>
            )}

            <div className={compact ? '' : 'max-w-7xl mx-auto relative'}>

                {compact ? (
                    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-50">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                                <Video size={15} className="text-white" />
                            </div>
                            <span className="font-bold text-gray-800 text-sm">My Videos</span>
                            <div className="flex items-center gap-2 ml-1">
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full border border-blue-100">
                                    <Video size={10} />{statsLoading ? '…' : totalVideos} total
                                </span>
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-purple-50 text-purple-700 text-xs font-semibold rounded-full border border-purple-100">
                                    <Calendar size={10} />{statsLoading ? '…' : (stats?.videos_this_month ?? '—')} this month
                                </span>
                                <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-0.5 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full border border-teal-100">
                                    <Clock size={10} />{statsLoading ? '…' : formatTotalDuration(stats?.total_duration_seconds)} recorded
                                </span>
                            </div>
                            <button onClick={onViewAll}
                                className="ml-auto flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-s font-semibold rounded-lg hover:opacity-90 transition shadow-sm flex-shrink-0">
                                Open Dashboard <ChevronRight size={16} />
                            </button>
                        </div>
                        {loading && videos.length === 0 ? (
                            <div className="flex">{[...Array(6)].map((_, i) => <div key={i} className="flex-1 h-16 bg-gray-100 animate-pulse border-r border-white last:border-r-0" />)}</div>
                        ) : videos.length === 0 ? (
                            <div className="px-4 py-3 text-xs text-gray-400 italic">No videos yet — record your first review with the AIP Mobile App or Chrome Extension.</div>
                        ) : (
                            <div className="flex overflow-hidden">
                                {videos.slice(0, 6).map(video => (
                                    <button key={video.id} onClick={onViewAll} title={video.review_title || video.product_title || video.asin}
                                        className="relative flex-1 group overflow-hidden border-r border-white last:border-r-0 focus:outline-none" style={{ minWidth: 0, height: 64 }}>
                                        {video.product_image ? (
                                            <img src={video.product_image} alt="" className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300" />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                                                <Video size={16} className="text-blue-400" />
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200" />
                                        {video.duration_seconds != null && (
                                            <span className="absolute bottom-1 left-1 text-[9px] font-bold text-white bg-black/60 px-1 rounded leading-4">{formatDuration(video.duration_seconds)}</span>
                                        )}
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                            <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
                                                <Play size={10} className="text-blue-600 ml-0.5" />
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                {totalVideos > 6 && (
                                    <button onClick={onViewAll}
                                        className="flex-shrink-0 w-14 h-16 bg-gradient-to-br from-blue-600 to-purple-600 flex flex-col items-center justify-center gap-0.5 border-l border-white hover:opacity-90 transition focus:outline-none">
                                        <span className="text-white text-xs font-bold leading-none">+{totalVideos - 6}</span>
                                        <span className="text-white/70 text-[9px] leading-none">more</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="mb-8">
                            <button onClick={() => navigate('/dashboard')}
                                className="flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 transition mb-4 font-medium">
                                <ArrowLeft size={16} /> Back to Dashboard
                            </button>
                            <div className="mb-6 flex items-start gap-3 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-2xl shadow-sm block">
                                <span className="text-xl flex-shrink-0">💡</span>
                                <p className="text-sm text-amber-800 font-medium leading-snug contents">
                                    Go to{' '}
                                    <a href="https://amazon.com/create/post" target="_blank" rel="noopener noreferrer"
                                        className="font-bold underline underline-offset-2 hover:text-amber-900 transition-colors contents">
                                        amazon.com/create/post
                                    </a>
                                    {' '}and use our <span className="font-bold contents">Chrome Extension</span> to auto-upload your videos with auto title, auto ASIN tagging, overlays, and more!
                                </p>
                            </div>
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                                        <Video className="text-blue-600" size={36} /> My Videos
                                    </h1>
                                    <p className="text-gray-500 mt-1">All your AIP review videos in one place</p>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button onClick={handleOpenRecovery}
                                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:opacity-90 transition text-sm font-semibold shadow-sm">
                                        <HardDriveDownload size={15} /> Recovery
                                    </button>
                                    <button onClick={handleDownloadAll} disabled={downloadingAll}
                                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:opacity-90 transition text-sm font-semibold shadow-sm disabled:opacity-50">
                                        {downloadingAll ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                                        {downloadingAll ? 'Downloading...' : 'Download All'}
                                    </button>

                                    {/* ── My Video List button ── */}
                                    <button
                                        onClick={() => setRecordingListPickerOpen(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-xl hover:opacity-90 transition text-sm font-semibold shadow-sm"
                                    >
                                        <Tag size={15} /> My Video List
                                    </button>

                                    {/* ── Bulk Upload button ── */}
                                    <button
                                        onClick={() => setBulkUploadOpen(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-xl hover:opacity-90 transition text-sm font-semibold shadow-sm"
                                    >
                                        <Files size={15} /> Bulk Upload
                                    </button>

                                    {/* ── Single Upload button ── */}
                                    <label className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-xl hover:opacity-90 transition text-sm font-semibold shadow-sm ${uploadingVideo ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                        {uploadingVideo ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                                        {uploadingVideo ? 'Uploading...' : 'Upload Video'}
                                        <input type="file" accept="video/*" className="hidden" disabled={uploadingVideo} onChange={handleUploadVideo} />
                                    </label>

                                    <button onClick={() => { fetchStats(); fetchVideos(1, search); }}
                                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition text-sm font-medium shadow-sm">
                                        <RefreshCw size={15} /> Refresh
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <StatCard icon={Video} label="Total Videos" value={statsLoading ? '…' : (stats?.total_videos ?? '—')} sub="all time" gradient="bg-gradient-to-br from-blue-500 to-blue-700" />
                            <StatCard icon={Calendar} label="This Month" value={statsLoading ? '…' : (stats?.videos_this_month ?? '—')} sub="recorded" gradient="bg-gradient-to-br from-purple-500 to-purple-700" />
                            <StatCard icon={Clock} label="Total Runtime" value={statsLoading ? '…' : formatTotalDuration(stats?.total_duration_seconds)} sub="of content" gradient="bg-gradient-to-br from-teal-500 to-teal-700" />
                            <StatCard icon={Tag} label="Top Category" value={statsLoading ? '…' : (stats?.top_category ?? '—')} sub="most reviewed" gradient="bg-gradient-to-br from-indigo-500 to-indigo-700" />
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
                            <form onSubmit={handleSearch} className="flex gap-3 flex-wrap">
                                <div className="flex-1 min-w-[200px] relative">
                                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                                        placeholder="Search by ASIN, product, review title…"
                                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent" />
                                    {searchInput && (
                                        <button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={14} /></button>
                                    )}
                                </div>
                                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                                    className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white min-w-[140px]">
                                    <option value="all">All Categories</option>
                                    {allCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                                    className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white min-w-[130px]">
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                    <option value="longest">Longest First</option>
                                    <option value="shortest">Shortest First</option>
                                </select>
                                <button type="submit"
                                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition shadow-sm flex items-center gap-2">
                                    <Search size={14} /> Search
                                </button>
                            </form>
                            {(search || categoryFilter !== 'all' || sortBy !== 'newest') && (
                                <div className="flex gap-2 mt-3 flex-wrap">
                                    {search && <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">"{search}" <button onClick={clearSearch}><X size={11} /></button></span>}
                                    {categoryFilter !== 'all' && <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full border border-purple-200">{categoryFilter} <button onClick={() => setCategoryFilter('all')}><X size={11} /></button></span>}
                                    {sortBy !== 'newest' && <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full border border-gray-200">{sortBy} <button onClick={() => setSortBy('newest')}><X size={11} /></button></span>}
                                </div>
                            )}
                        </div>

                        <p className="text-sm text-gray-500 font-medium mb-4">
                            {loading && videos.length === 0 ? 'Loading…' : `${(!search && categoryFilter === 'all' && totalVideos) || displayVideos.length} video${displayVideos.length !== 1 ? 's' : ''}${search ? ` for "${search}"` : ''}`}
                        </p>

                        {!loading && displayVideos.length === 0 && (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 py-20 text-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Video size={28} className="text-blue-400" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-700 mb-2">{search ? `No videos found for "${search}"` : 'No videos yet'}</h3>
                                <p className="text-gray-400 text-sm max-w-xs mx-auto">
                                    {search ? 'Try a different search term or clear your filters.' : 'Record your first product review using the AIP Mobile App or Chrome Extension.'}
                                </p>
                                {search && <button onClick={clearSearch} className="mt-4 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition">Clear Search</button>}
                            </div>
                        )}

                        {displayVideos.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {displayVideos.map(video => (
                                    <div key={video.id} className="video-card-enter">
                                        <VideoCard key={video.id} video={video} user={user} onDelete={handleDelete} onDownload={handleDownload}
                                            onOverdub={setOverdubReview} converting={converting} onEdit={setEditReview}
                                            onEditTitle={openEditTitle} onEditAsins={openEditAsins}
                                            onStatusChange={(id, status) => setVideos(prev => prev.map(v => v.id === id ? { ...v, status } : v))} />
                                    </div>
                                ))}
                            </div>
                        )}

                        {loading && videos.length === 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden animate-pulse">
                                        <div className="h-12 bg-gray-100" />
                                        <div className="bg-gray-200" style={{ aspectRatio: '16/9' }} />
                                        <div className="h-8 bg-gray-50 border-t border-gray-100" />
                                        <div className="h-8 bg-white border-t border-gray-50" />
                                    </div>
                                ))}
                            </div>
                        )}

                        {hasMore && !loading && (
                            <div className="text-center mt-8">
                                <button onClick={loadMore}
                                    className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition shadow-lg">
                                    <ChevronDown size={16} /> Load More Videos
                                </button>
                            </div>
                        )}

                        {loading && videos.length > 0 && (
                            <div className="text-center mt-6 text-gray-400 text-sm flex items-center justify-center gap-2">
                                <Loader2 size={16} className="animate-spin" /> Loading more…
                            </div>
                        )}
                    </>
                )}
            </div>

            {overdubReview && (
                <VideoOverdub review={overdubReview} onClose={() => setOverdubReview(null)}
                    onSaved={() => { setOverdubReview(null); setPage(1); fetchVideos(1, search); }} />
            )}

            {editReview && (
                <VideoEditor review={editReview} onClose={() => setEditReview(null)}
                    onSaved={() => { setEditReview(null); fetchVideos(1, search); }} />
            )}

            {/* ── Edit Title Modal ── */}
            {editTitleReview && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
                    onClick={() => { if (!editTitleSaving) setEditTitleReview(null); }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h2 className="text-base font-bold text-gray-800">Edit Title</h2>
                            <button onClick={() => setEditTitleReview(null)} disabled={editTitleSaving} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                        </div>
                        <div className="px-6 py-5">
                            <input
                                autoFocus
                                value={editTitleValue}
                                onChange={e => setEditTitleValue(e.target.value)}
                                maxLength={60}
                                onKeyDown={e => { if (e.key === 'Enter') handleEditTitleSave(); }}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                            <p className="text-[10px] text-gray-400 mt-1">{editTitleValue.length}/60 characters</p>
                        </div>
                        <div className="flex gap-2 px-6 pb-5">
                            <button onClick={() => setEditTitleReview(null)} disabled={editTitleSaving}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition font-medium disabled:opacity-50">
                                Cancel
                            </button>
                            <button onClick={handleEditTitleSave} disabled={editTitleSaving || !editTitleValue.trim()}
                                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-violet-600 text-white text-sm font-semibold hover:opacity-90 transition shadow-sm disabled:opacity-50 flex items-center justify-center gap-2">
                                {editTitleSaving ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />}
                                {editTitleSaving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Edit ASINs Modal ── */}
            {editAsinsReview && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
                    onClick={() => { if (!editAsinsSaving) setEditAsinsReview(null); }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                            <div>
                                <h2 className="text-base font-bold text-gray-800">Edit ASINs</h2>
                                <p className="text-xs text-gray-400 truncate max-w-xs">{editAsinsReview.review_title || editAsinsReview.product_title}</p>
                            </div>
                            <button onClick={() => setEditAsinsReview(null)} disabled={editAsinsSaving} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                        </div>
                        <div className="px-6 py-4 flex-shrink-0">
                            <div className="flex gap-2">
                                <input
                                    value={editAsinsCustomInput}
                                    onChange={e => setEditAsinsCustomInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomEditAsin(); } }}
                                    placeholder="Add ASINs, comma separated…"
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono"
                                />
                                <button onClick={handleAddCustomEditAsin}
                                    className="px-4 py-2 bg-purple-100 text-purple-700 text-sm font-semibold rounded-xl hover:bg-purple-200 transition">
                                    Add
                                </button>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1">First selected ASIN = primary. Others = compare ASINs.</p>
                            <button
                                onClick={() => setEditAsinsShowListPicker(true)}
                                className="mt-2 w-full py-2 rounded-xl border-2 border-dashed border-teal-200 text-sm text-teal-600 hover:border-teal-400 hover:bg-teal-50 transition font-medium"
                            >
                                📋 Pick from Recording List
                            </button>
                            {editAsinsShowListPicker && (
                                <RecordingListPickerModal
                                    user={user}
                                    selectOnly
                                    onClose={() => setEditAsinsShowListPicker(false)}
                                    onMarkedRecorded={(items) => {
                                        const arr = Array.isArray(items) ? items : [items];
                                        const newAsins = arr.map(i => i.asin).filter(Boolean).map(a => a.toUpperCase());
                                        if (newAsins.length) {
                                            setEditAsinsAll(prev => [...new Set([...prev, ...newAsins])]);
                                            setEditAsinsSelected(prev => new Set([...prev, ...newAsins].slice(0, 15)));
                                        }
                                        setEditAsinsShowListPicker(false);
                                    }}
                                />
                            )}
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 pb-2 space-y-2">
                            {editAsinsAll.map(asin => {
                                const isSelected = editAsinsSelected.has(asin);
                                const d = editAsinsDetails[asin];
                                return (
                                    <div key={asin} onClick={() => toggleEditAsin(asin)}
                                        className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition ${isSelected ? 'bg-teal-50 border-teal-300' : 'bg-gray-50 border-gray-100 hover:border-gray-300'}`}>
                                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition ${isSelected ? 'bg-teal-500 border-teal-500' : 'border-gray-300'}`}>
                                            {isSelected && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                                        </div>
                                        {d?.image ? (
                                            <img src={d.image} alt="" className="w-10 h-10 rounded-lg object-contain bg-white border border-gray-100 flex-shrink-0" />
                                        ) : (
                                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-lg">📦</div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-gray-800 truncate">{d?.title || asin}</p>
                                            <p className="text-[10px] font-mono text-gray-400">{asin}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex gap-2 flex-shrink-0">
                            <button onClick={() => setEditAsinsReview(null)} disabled={editAsinsSaving}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition font-medium disabled:opacity-50">
                                Cancel
                            </button>
                            <button onClick={handleEditAsinsSave} disabled={editAsinsSaving}
                                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-sm font-semibold hover:opacity-90 transition shadow-sm disabled:opacity-50 flex items-center justify-center gap-2">
                                {editAsinsSaving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                                {editAsinsSaving ? 'Saving…' : `Save (${editAsinsSelected.size})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Single Upload Meta Modal ── */}
            {singleUploadFile && (
                <UploadMetaModal
                    file={singleUploadFile}
                    onConfirm={handleSingleUploadConfirm}
                    onCancel={() => setSingleUploadFile(null)}
                    user={user}
                />
            )}

            {/* ── My Video List (standalone browse) ── */}
            {recordingListPickerOpen && (
                <RecordingListPickerModal
                    user={user}
                    standalone
                    selectOnly
                    onClose={() => setRecordingListPickerOpen(false)}
                    onMarkedRecorded={() => setRecordingListPickerOpen(false)}
                />
            )}

            {/* ── Bulk Upload Modal ── */}
            {bulkUploadOpen && (
                <BulkUploadModal
                    onClose={() => setBulkUploadOpen(false)}
                    uploadSingleVideo={uploadSingleVideo}
                    user={user}
                    onComplete={() => {
                        fetchVideos(1, search);
                        fetchStats();
                    }}
                />
            )}

            {/* Recovery modal */}
            {recoveryOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setRecoveryOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col m-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                                    <HardDriveDownload size={20} className="text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-800">Video Recovery</h2>
                                    <p className="text-xs text-gray-400">Archived videos not yet in your library</p>
                                </div>
                            </div>
                            <button onClick={() => setRecoveryOpen(false)} className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            {recoveryLoading ? (
                                <div className="flex items-center justify-center py-12 text-gray-400 gap-2"><Loader2 size={20} className="animate-spin" /> Scanning archive…</div>
                            ) : recoveryFiles.length === 0 ? (
                                <div className="text-center py-12 text-gray-400 text-sm">No WebM files found in S3.</div>
                            ) : (
                                <div className="space-y-2">
                                    <p className="text-xs text-gray-500 font-medium mb-3">{recoveryFiles.length} file{recoveryFiles.length !== 1 ? 's' : ''} found</p>
                                    {recoveryFiles.slice((recoveryPage - 1) * 10, recoveryPage * 10).map(file => {
                                        const name = file.s3_key.split('/').pop();
                                        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
                                        const isRestoring = recoveryDownloading === file.s3_key;
                                        const recovered = recoveredFiles[file.s3_key];
                                        const isDone = recovered?.status === 'done' && recovered?.mp4_url;
                                        const isPending = recovered && !isDone;
                                        return (
                                            <div key={file.s3_key} className={`flex items-center gap-3 p-3 rounded-xl border transition ${isDone ? 'bg-green-50 border-green-200' : isPending ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100 hover:border-gray-200'}`}>
                                                <button onClick={() => setRecoveryPreview(file.s3_url)} className="relative w-16 h-16 rounded-lg overflow-hidden bg-black flex-shrink-0 group cursor-pointer">
                                                    <video src={file.s3_url} preload="metadata" muted className="w-full h-full object-cover" />
                                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/50 transition"><Play size={18} className="text-white" /></div>
                                                </button>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 truncate">{name}</p>
                                                    <div className="flex items-center gap-3 text-[10px] text-gray-400">
                                                        <span>{sizeMB} MB</span><span>{formatDate(file.last_modified)}</span>
                                                    </div>
                                                    {isPending && <p className="text-[10px] text-amber-600 font-semibold mt-0.5">Converting to MP4…</p>}
                                                    {isDone && <p className="text-[10px] text-green-600 font-semibold mt-0.5">Ready to download</p>}
                                                </div>
                                                {isDone ? (
                                                    <button onClick={() => handleRecoveryMp4Download(recovered.mp4_url, name.replace('.webm', '.mp4'))}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-semibold rounded-lg hover:opacity-90 transition shadow-sm">
                                                        <Download size={12} /> Download MP4
                                                    </button>
                                                ) : isPending ? (
                                                    <div className="flex items-center gap-1.5 px-3 py-1.5 text-amber-600 text-xs font-semibold">
                                                        <Loader2 size={12} className="animate-spin" /> Processing…
                                                    </div>
                                                ) : (
                                                    <button onClick={() => handleRecoveryRestore(file)} disabled={isRestoring}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-semibold rounded-lg hover:opacity-90 transition shadow-sm disabled:opacity-50">
                                                        {isRestoring ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                                        {isRestoring ? 'Restoring…' : 'Restore'}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {recoveryFiles.length > 10 && (
                                <div className="flex items-center justify-center gap-3 pt-3 border-t border-gray-100 mt-2">
                                    <button
                                        onClick={() => setRecoveryPage(p => Math.max(1, p - 1))}
                                        disabled={recoveryPage === 1}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                                        <ChevronLeft size={13} /> Prev
                                    </button>
                                    <span className="text-xs text-gray-500 font-medium">
                                        Page {recoveryPage} of {Math.ceil(recoveryFiles.length / 10)}
                                    </span>
                                    <button
                                        onClick={() => setRecoveryPage(p => Math.min(Math.ceil(recoveryFiles.length / 10), p + 1))}
                                        disabled={recoveryPage === Math.ceil(recoveryFiles.length / 10)}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                                        Next <ChevronRight size={13} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {recoveryPreview && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={() => setRecoveryPreview(null)}>
                    <div className="relative max-w-3xl w-auto mx-4" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setRecoveryPreview(null)} className="absolute -top-5 -right-5 text-white/80 hover:text-white transition"><X size={24} /></button>
                        <video src={recoveryPreview} controls autoPlay className="h-[80vh] rounded-xl shadow-2xl m-auto" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyVideosDashboard;